import { Query } from '@apollo/client/react/components';
import { KeySet } from '@tableau/data-structures';
import React from 'react';
import {
  FlowLineageCountsFilteredByFieldQuery, FlowLineageCountsFilteredByFieldQueryVariables, FlowLineageCountsQuery, FlowLineageCountsQueryVariables,
  FlowLineageCountsFilteredByFieldDocument,
  FlowLineageCountsDocument
} from '../../../../../gen-src/queries';
import { LineageCountsParentProps } from '../lineage/LineageCountsCacher';
import { renderFilteredLineageCounts, renderUnfilteredLineageCounts } from '../lineage/LineageCountsFunctions';

export type FlowLineageCounts = {
  readonly filteredFieldName: string | null;
  readonly upstreamDatabases: number;
  readonly upstreamTables: number;
  readonly upstreamVirtualConnections: number;
  readonly upstreamVirtualConnectionTables: number;
  readonly upstreamFlows: number;
  readonly upstreamDatasources: number;
  readonly downstreamDashboards: number;
  readonly downstreamDatabases: number;
  readonly downstreamTables: number;
  readonly downstreamFlows: number;
  readonly downstreamDatasources: number;
  readonly downstreamLenses: number;
  readonly downstreamWorkbooks: number;
  readonly downstreamSheets: number;
  readonly downstreamMetrics: number;
  readonly downstreamOwners: number;
};

function createUnfilteredCounts(data: FlowLineageCountsQuery): FlowLineageCounts | null {
  const flow = data.flows && data.flows[0];
  if (flow
    && flow.upstreamDatabasesConnection
    && flow.upstreamTablesConnection
    && flow.upstreamFlowsConnection
    && flow.upstreamDatasourcesConnection
    && flow.downstreamDashboardsConnection
    && flow.downstreamDatabasesConnection
    && flow.downstreamTablesConnection
    && flow.downstreamFlowsConnection
    && flow.downstreamDatasourcesConnection
    && flow.downstreamWorkbooksConnection
    && flow.downstreamSheetsConnection
    && flow.downstreamMetricsConnection
    && flow.downstreamOwnersConnection
  ) {
    return {
      filteredFieldName: null,
      upstreamDatabases: flow.upstreamDatabasesConnection.totalCount,
      upstreamTables: flow.upstreamTablesConnection.totalCount,
      upstreamVirtualConnections: flow.upstreamVirtualConnectionsConnection?.totalCount || 0,
      upstreamVirtualConnectionTables: flow.upstreamVirtualConnectionTablesConnection?.totalCount || 0,
      upstreamFlows: flow.upstreamFlowsConnection.totalCount,
      upstreamDatasources: flow.upstreamDatasourcesConnection.totalCount,
      downstreamDashboards: flow.downstreamDashboardsConnection.totalCount,
      downstreamDatabases: flow.downstreamDatabasesConnection.totalCount,
      downstreamTables: flow.downstreamTablesConnection.totalCount,
      downstreamFlows: flow.downstreamFlowsConnection.totalCount,
      downstreamDatasources: flow.downstreamDatasourcesConnection.totalCount,
      downstreamLenses: flow.downstreamLensesConnection?.totalCount || 0,
      downstreamWorkbooks: flow.downstreamWorkbooksConnection.totalCount,
      downstreamSheets: flow.downstreamSheetsConnection.totalCount,
      downstreamMetrics: flow.downstreamMetricsConnection.totalCount,
      downstreamOwners: flow.downstreamOwnersConnection.totalCount
    };
  }
  return null;
}

function createFilteredCounts(data: FlowLineageCountsFilteredByFieldQuery): FlowLineageCounts | null {
  const flow = data.flows && data.flows[0];
  const outputStep = flow && flow.outputSteps && flow.outputSteps[0];
  const filteredField = outputStep && outputStep.filteredFieldsConnection &&
    outputStep.filteredFieldsConnection.nodes[0];
  if (outputStep
    && filteredField
    && filteredField.upstreamDatabasesConnection
    && filteredField.upstreamTablesConnection
    && filteredField.upstreamFlowsConnection
    && filteredField.upstreamDatasourcesConnection
    && filteredField.downstreamDashboardsConnection
    && filteredField.downstreamDatabasesConnection
    && filteredField.downstreamTablesConnection
    && filteredField.downstreamFlowsConnection
    && filteredField.downstreamDatasourcesConnection
    && filteredField.downstreamLensesConnection
    && filteredField.downstreamWorkbooksConnection
    && filteredField.downstreamSheetsConnection
    && filteredField.downstreamMetricsConnection
    && filteredField.downstreamOwnersConnection
  ) {
    return {
      filteredFieldName: filteredField?.name ?? null,
      upstreamDatabases: filteredField.upstreamDatabasesConnection.totalCount,
      upstreamTables: filteredField.upstreamTablesConnection.totalCount,
      upstreamVirtualConnections: filteredField.upstreamVirtualConnectionsConnection?.totalCount || 0,
      upstreamVirtualConnectionTables: filteredField.upstreamVirtualConnectionTablesConnection?.totalCount || 0,
      upstreamFlows: filteredField.upstreamFlowsConnection.totalCount,
      upstreamDatasources: filteredField.upstreamDatasourcesConnection.totalCount,
      downstreamDashboards: filteredField.downstreamDashboardsConnection.totalCount,
      downstreamDatabases: filteredField.downstreamDatabasesConnection.totalCount,
      downstreamTables: filteredField.downstreamTablesConnection.totalCount,
      downstreamFlows: filteredField.downstreamFlowsConnection.totalCount,
      downstreamDatasources: filteredField.downstreamDatasourcesConnection.totalCount,
      downstreamLenses: filteredField.downstreamLensesConnection.totalCount,
      downstreamWorkbooks: filteredField.downstreamWorkbooksConnection.totalCount,
      downstreamSheets: filteredField.downstreamSheetsConnection.totalCount,
      downstreamMetrics: filteredField.downstreamMetricsConnection.totalCount,
      downstreamOwners: filteredField.downstreamOwnersConnection.totalCount
    };
  }
  return null;
}

export type FlowLineageCountsQueryWrapperProps = {
  readonly flowVizportalUrlId: string;
  readonly selectedOutputStepId: string;
  readonly selectedKeys: KeySet;
} & LineageCountsParentProps<FlowLineageCounts>;

export const FlowLineageCountsQueryWrapper = (props: FlowLineageCountsQueryWrapperProps) => {
  const keys = Object.keys(props.selectedKeys);
  if (keys.length > 0) {
    const selectedFieldId = keys[0];
    return (
      <Query<FlowLineageCountsFilteredByFieldQuery, FlowLineageCountsFilteredByFieldQueryVariables>
        query={FlowLineageCountsFilteredByFieldDocument}
        variables={{
          flowVizportalUrlId: props.flowVizportalUrlId,
          outputStepId: props.selectedOutputStepId,
          selectedFieldId: selectedFieldId,
        }}
      >
        {result => <>{renderFilteredLineageCounts(result, createUnfilteredCounts, createFilteredCounts, props.children)}</>}
      </Query>
    );
  } else {
    return (
      <Query<FlowLineageCountsQuery, FlowLineageCountsQueryVariables>
        query={FlowLineageCountsDocument}
        variables={{
          flowVizportalUrlId: props.flowVizportalUrlId,
        }}
      >
        {result => <>{renderUnfilteredLineageCounts(result, createUnfilteredCounts, props.children)}</>}
      </Query>
    );
  }
}
